/**
 * The `topdown-2d` LevelKind (design §15) — a floor-plan level for top-down
 * action-adventure (StarTropics-like). Registered via the §14.2 seam: no changes to any
 * other kind. Consumes the topdown variant of the 3Cs union.
 */
import type { LevelFinding, LevelKind, LevelValidationCtx } from '../../types.js';
import { TopdownThreeCsSchema } from '../../three-cs.js';
import { type TopdownLevel, TopdownLevelSchema } from './schema.js';
import { compileTopdownGrammar, validateTopdown } from './validate.js';
import { checkRefs, topdownRefs } from '../../ref-integrity.js';

export const topdownLevelKind: LevelKind<TopdownLevel> = {
  id: 'topdown-2d',
  schema: TopdownLevelSchema,
  consumesThreeCs: true,
  view: 'viewport-2d3d',
  derivation: {
    genres: ['action-adventure', 'dungeon-crawl', 'top-down'],
    mechanicHints: ['rooms', 'doors', 'keys', 'explore', 'dungeon', 'overworld'],
  },
  validate(data: TopdownLevel, ctx: LevelValidationCtx): LevelFinding[] {
    const parsed = TopdownThreeCsSchema.safeParse(ctx.threeCs);
    if (!parsed.success) {
      return [
        {
          severity: 'error',
          code: 'missing-three-cs',
          message:
            'topdown-2d requires a topdown-2d 3Cs profile in ctx.threeCs (the game feel is set to a different archetype)',
        },
      ];
    }
    return [
      ...validateTopdown(data, compileTopdownGrammar(parsed.data)),
      ...checkRefs(topdownRefs(data), ctx.palette),
    ];
  },
};

export { TopdownLevelSchema, ROOM_KINDS } from './schema.js';
export type { TopdownLevel, TopdownRoom, TopdownCorridor, RoomKind } from './schema.js';
export { compileTopdownGrammar, reachabilityFixpoint, type TopdownGrammar } from './validate.js';
